import { useFeatureGate } from '@statsig/react-bindings';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { MeasureSchemaType } from '@grow-therapy-team/seedling-components';

import { TelehealthSessionInfoData_TelehealthSessionInfo_Appointment } from '../../hooks';
import { Statsig, StatsigLoadingContext } from '../../statsig/StatsigProvider';
import {
  delayedEntryGate,
  telehealth4MinMeasureRestrictionDisabledGate,
  telehealthAutoOpenIncompleteMeasuresDrawer,
} from '../../statsig/gates';
import { useGetShouldShowReflectionTopics } from '../reflections/useGetShouldShowReflectionTopics';
import { VisitorDrawerState, doesClientRequireSafetyGuardsAtom, visitorMeasuresAtom } from '../state';
import { JoinSessionCalloutWrapper as JoinSessionCallout } from './delayed-entry/JoinSessionCallout';
import { deadlineAtom } from './delayed-entry/state';
import {
  PreSessionPromptWrapper as PreSessionPrompt,
  PreSessionPromptVariant,
} from './pre-session-prompt/PreSessionPrompt';
import { usePreSessionPromptState } from './pre-session-prompt/usePreSessionPromptState';
import { useTransitionToMeetingRoom } from './useWatchForRoomToken';

const PRE_SESSION_PROMPT_THRESHOLD_MINUTES = 4;

type SessionInfoSectionWrapperProps = {
  appointment?: TelehealthSessionInfoData_TelehealthSessionInfo_Appointment | null;
  openDrawer: (state: VisitorDrawerState) => void;
};

function isWithinThreshold(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  const isWithinThreshold =
    differenceInMinutes(parseISO(appointmentStartTime), new Date()) > PRE_SESSION_PROMPT_THRESHOLD_MINUTES;
  return isWithinThreshold;
}

function appointmentHasStarted(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  return Date.now() > new Date(appointmentStartTime).getTime();
}
export function SessionInfoSectionWrapper({ appointment, ...props }: SessionInfoSectionWrapperProps) {
  const { shouldShowReflectionTopics, isLoading: isLoadingShouldShowReflectionTopics } =
    useGetShouldShowReflectionTopics();
  const visitorMeasures = useAtomValue(visitorMeasuresAtom);
  const hasForms = !!visitorMeasures?.length;
  const formsAreLoading = visitorMeasures === undefined;
  const isLoading = useContext(StatsigLoadingContext) || isLoadingShouldShowReflectionTopics || formsAreLoading;
  const { preSessionPrompt, setPreSessionPrompt, clearPreSessionPrompt } = usePreSessionPromptState();
  const hasClearedLocalStorageRef = useRef(false); // to prevent re-computation
  const deadline = useAtomValue(deadlineAtom);
  const { value: is4MinRestrictionDisabled } = useFeatureGate(telehealth4MinMeasureRestrictionDisabledGate);
  const { value: isAutoOpenMeasuresDrawerEnabled } = useFeatureGate(telehealthAutoOpenIncompleteMeasuresDrawer);

  const measures = visitorMeasures ?? [];
  const visitorMeasuresAreAllScreeners =
    measures.length > 0 &&
    measures.reduce(
      (prev, currMeasure) =>
        prev &&
        !!currMeasure &&
        (currMeasure.schema.type === MeasureSchemaType.GAD7_WITH_SCREENER ||
          currMeasure.schema.type === MeasureSchemaType.PHQ9_WITH_SCREENER),
      true,
    );

  const transitionToMeetingRoom = useTransitionToMeetingRoom();
  const getDoesClientRequireSafetyGuards = useAtomCallback(
    useCallback((get) => get(doesClientRequireSafetyGuardsAtom), []),
  );

  const joinCallback = useCallback(() => {
    transitionToMeetingRoom({
      onDone() {
        hasClearedLocalStorageRef.current = true;
        clearPreSessionPrompt();
      },
    });
  }, [clearPreSessionPrompt, transitionToMeetingRoom]);

  useEffect(
    function setPreSessionPromptState() {
      if (hasClearedLocalStorageRef.current) return;
      if (isLoading) return;
      const hasAppointment = !!appointment?.timeStart;
      const doesClientRequireSafetyGuards = getDoesClientRequireSafetyGuards();

      const shouldShowScreener = !appointmentHasStarted(appointment?.timeStart) && visitorMeasuresAreAllScreeners;

      const shouldShowClientMeasures =
        !isAutoOpenMeasuresDrawerEnabled &&
        (is4MinRestrictionDisabled ||
          (hasAppointment && isWithinThreshold(appointment?.timeStart)) ||
          shouldShowScreener) &&
        hasForms &&
        !doesClientRequireSafetyGuards;

      // Priority order:
      // 1. Measures CTA (only when auto-open drawer gate is disabled)
      // 2. Reflection Topics (if the user is authenticated and the feature gate
      //    is enabled and the user has reflection topics)
      // 3. Vibes (if no other pre-session prompt applies). This is the only
      //    option for clients who require safety guards, who are not eligible
      //    to fill out any forms.

      if (preSessionPrompt !== undefined) {
        // once set, always set
        return;
      } else if (shouldShowClientMeasures) {
        setPreSessionPrompt(PreSessionPromptVariant.CLIENT_MEASURES);
      } else if (shouldShowReflectionTopics && hasAppointment && !doesClientRequireSafetyGuards) {
        setPreSessionPrompt(PreSessionPromptVariant.REFLECTION_TOPICS);
      } else {
        setPreSessionPrompt(PreSessionPromptVariant.VIBES);
      }
    },
    [
      isLoading,
      isAutoOpenMeasuresDrawerEnabled,
      is4MinRestrictionDisabled,
      appointment?.timeStart,
      hasForms,
      preSessionPrompt,
      setPreSessionPrompt,
      visitorMeasuresAreAllScreeners,
      shouldShowReflectionTopics,
      getDoesClientRequireSafetyGuards,
    ],
  );

  if (!isLoading && preSessionPrompt !== undefined) {
    return (
      <div className='flex flex-col items-center gap-y-2 p-4'>
        {Statsig.checkGate(delayedEntryGate) && deadline && (
          <JoinSessionCallout deadline={deadline} joinCallback={joinCallback} />
        )}
        <PreSessionPrompt variant={preSessionPrompt} {...props} />
      </div>
    );
  }
}
